import { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import logout from '../../../assets/logout.png'
import { FaCog, FaChevronDown, FaAngleDoubleLeft, FaAngleDoubleRight, FaUserCircle } from 'react-icons/fa'
import {
    useAuthUserData,
    useSetActiveOrganisation,
    useUpdateAuthUserData,
    useUserOrganisations,
} from '../../../authUserProvider'
import { SidebarDisplay } from '../../../helpers/sidebarDisplay'
import { getLogoutUrl } from '../../../helpers/authConfig'
import { useSidebarCollapsed } from '../../../helpers/sidebarCollapse'
import { useBranding } from '../../../branding/useBranding'
import styles from './styles.module.scss'

export const PROFILE_PATH = '/profile'

function Sidebar() {
    const authUser = useAuthUserData()
    const updateAuthUser = useUpdateAuthUserData()
    const { organisations, activeOrganisation, primary } = useUserOrganisations()
    const setActiveOrganisation = useSetActiveOrganisation()
    const brand = useBranding()
    const navigate = useNavigate()
    const location = useLocation()
    const [collapsed, setCollapsed] = useSidebarCollapsed()
    // After clicking "collapse" the pointer is still over the sidebar, which
    // would immediately re-expand it via the hover peek. Suppress the peek
    // until the pointer leaves once so the click has a visible effect.
    const [peekSuppressed, setPeekSuppressed] = useState(false)
    const [displaySidebar, setDisplaySidebar] = useState(false)
    const [username, setUsername] = useState('')
    const [usernameChar, setUsernameChar] = useState('')
    const [showUserMenu, setShowUserMenu] = useState(false)
    const [showOrgSwitcher, setShowOrgSwitcher] = useState(false)
    const userMenuRef = useRef<HTMLDivElement>(null)
    const userMenuTriggersRef = useRef<HTMLDivElement>(null)
    const orgSwitcherRef = useRef<HTMLDivElement>(null)
    const orgSwitcherButtonRef = useRef<HTMLButtonElement>(null)
    const collapseButtonRef = useRef<HTMLButtonElement>(null)

    // The sidebar tracks the *active* org, not the user's Cognito primary.
    // For single-org users this is the same value; for multi-org users this
    // changes when they pick a different org from the switcher below.
    // Fallback to the Cognito attribute on first paint before /init resolves.
    const sidebarOrg = activeOrganisation || authUser?.['custom:organisation'] || ''
    const hasMultipleOrgs = organisations.length > 1

    useEffect(() => {
        if (authUser) {
            setDisplaySidebar(authUser.sidebar || false)

            // Follow name edits made on the profile page (authUser.name is
            // updated there), not just the first value we see.
            const name = String(authUser.name || '')
            setUsername(name)
            setUsernameChar(name.charAt(0).toLocaleUpperCase())
        }
    }, [authUser])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node
            if (
                showUserMenu &&
                userMenuRef.current &&
                !userMenuRef.current.contains(target) &&
                userMenuTriggersRef.current &&
                !userMenuTriggersRef.current.contains(target)
            ) {
                setShowUserMenu(false)
            }
            if (
                showOrgSwitcher &&
                orgSwitcherRef.current &&
                !orgSwitcherRef.current.contains(target) &&
                orgSwitcherButtonRef.current &&
                !orgSwitcherButtonRef.current.contains(target)
            ) {
                setShowOrgSwitcher(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showUserMenu, showOrgSwitcher])

    // Close popovers whenever the route changes (e.g. after "View profile").
    useEffect(() => {
        setShowUserMenu(false)
        setShowOrgSwitcher(false)
    }, [location.pathname])

    // Belt-and-braces for the peek suppression: `onMouseLeave` on the sidebar
    // is the primary reset, but if the pointer is already elsewhere (or the
    // leave event is missed) the first movement outside the rail re-arms it.
    useEffect(() => {
        if (!peekSuppressed) return
        const onMove = (event: PointerEvent) => {
            const target = event.target as HTMLElement | null
            if (!target?.closest?.('#sidebar')) setPeekSuppressed(false)
        }
        document.addEventListener('pointermove', onMove)
        return () => document.removeEventListener('pointermove', onMove)
    }, [peekSuppressed])

    const handleClose = (event: React.MouseEvent) => {
        if ((event.target as HTMLElement).closest('#sidebar')) {
            return
        }

        updateAuthUser({ ...authUser, sidebar: false })
    }

    const toggleSidebar = () => {
        updateAuthUser({ ...authUser, sidebar: !displaySidebar })
    }

    const toggleCollapsed = () => {
        const next = !collapsed
        setCollapsed(next)
        setShowUserMenu(false)
        setShowOrgSwitcher(false)
        if (next) {
            setPeekSuppressed(true)
            // Keyboard focus would otherwise keep the rail expanded via :focus-within.
            collapseButtonRef.current?.blur()
        }
    }

    const toggleUserMenu = () => {
        setShowOrgSwitcher(false)
        setShowUserMenu((open) => !open)
    }

    const handleSwitchOrg = (org: string) => {
        setActiveOrganisation(org)
        setShowOrgSwitcher(false)
    }

    const goToProfile = () => {
        setShowUserMenu(false)
        if (authUser?.sidebar) {
            updateAuthUser({ ...authUser, sidebar: false })
        }
        navigate(PROFILE_PATH)
    }

    const sidebarClass = [
        styles.sidebar,
        displaySidebar ? styles.showSidebar : '',
        collapsed ? styles.collapsed : '',
        collapsed && peekSuppressed ? styles.noPeek : '',
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <div
            id="sidebarContainer"
            className={`${styles.sidebarContainer} ${displaySidebar ? styles.showSidebarContainer : ''}`}
            onClick={handleClose}
        >
            <div
                id="sidebar"
                className={sidebarClass}
                data-collapsed={collapsed || undefined}
                onMouseLeave={() => setPeekSuppressed(false)}
            >
                <div className={styles.sidebarHeader}>
                    <div className={styles.logoContainer}>
                        <img src={brand.logo} alt={brand.logoAlt} />
                    </div>
                    <button
                        ref={collapseButtonRef}
                        type="button"
                        className={styles.collapseToggle}
                        onClick={toggleCollapsed}
                        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        aria-pressed={collapsed}
                        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        {collapsed ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />}
                    </button>
                    <button className={styles.mobileCloseBtn} onClick={toggleSidebar} aria-label="Close sidebar">
                        ×
                    </button>
                </div>

                <div className={styles.sidebarLinks}>{SidebarDisplay(sidebarOrg)}</div>

                <div className={styles.sidebarFooter}>
                    <div className={styles.userInfo} ref={userMenuTriggersRef}>
                        <button
                            type="button"
                            className={styles.userAvatar}
                            onClick={toggleUserMenu}
                            aria-label="Open account menu"
                            aria-haspopup="menu"
                            aria-expanded={showUserMenu}
                            title={username || 'Account'}
                        >
                            <span className={styles.userInitials}>{usernameChar}</span>
                        </button>
                        <div className={styles.userDetails}>
                            <button
                                type="button"
                                className={styles.userName}
                                onClick={toggleUserMenu}
                                aria-haspopup="menu"
                                aria-expanded={showUserMenu}
                                title="Account"
                            >
                                {username}
                            </button>
                            {hasMultipleOrgs ? (
                                <button
                                    ref={orgSwitcherButtonRef}
                                    type="button"
                                    className={styles.orgSwitcherButton}
                                    onClick={() => {
                                        setShowUserMenu(false)
                                        setShowOrgSwitcher((s) => !s)
                                    }}
                                    aria-haspopup="listbox"
                                    aria-expanded={showOrgSwitcher}
                                    title="Switch organisation"
                                >
                                    <span className={styles.orgSwitcherLabel}>{sidebarOrg}</span>
                                    <FaChevronDown />
                                </button>
                            ) : (
                                <span className={styles.userRole}>{sidebarOrg}</span>
                            )}
                        </div>
                        <button
                            type="button"
                            className={styles.settingsButton}
                            onClick={toggleUserMenu}
                            aria-label="User settings"
                            aria-haspopup="menu"
                            aria-expanded={showUserMenu}
                        >
                            <FaCog />
                        </button>
                    </div>
                    {showOrgSwitcher && hasMultipleOrgs && (
                        <div ref={orgSwitcherRef} className={styles.orgSwitcherMenu} role="listbox">
                            {organisations.map((org) => (
                                <button
                                    key={org}
                                    type="button"
                                    onClick={() => handleSwitchOrg(org)}
                                    className={`${styles.orgSwitcherOption} ${
                                        org === sidebarOrg ? styles.orgSwitcherOptionActive : ''
                                    }`}
                                    role="option"
                                    aria-selected={org === sidebarOrg}
                                >
                                    <span className={styles.orgSwitcherOptionLabel}>{org}</span>
                                    {org === primary && <span className={styles.orgSwitcherPrimaryBadge}>Primary</span>}
                                </button>
                            ))}
                        </div>
                    )}
                    {showUserMenu && (
                        <div ref={userMenuRef} className={styles.userMenu} role="menu">
                            <div className={styles.userMenuHeader}>
                                <span className={styles.userMenuName}>{username}</span>
                                {authUser?.email && <span className={styles.userMenuEmail}>{authUser.email}</span>}
                            </div>
                            <button
                                type="button"
                                role="menuitem"
                                className={`${styles.userMenuItem} ${
                                    location.pathname === PROFILE_PATH ? styles.userMenuItemActive : ''
                                }`}
                                onClick={goToProfile}
                            >
                                <FaUserCircle />
                                <span>View profile</span>
                            </button>
                            <div className={styles.userMenuDivider} />
                            <a
                                href={getLogoutUrl()}
                                role="menuitem"
                                className={styles.logoutButton}
                                onClick={() => {
                                    // Session now lives in localStorage (shared
                                    // across tabs), so clear it on logout rather
                                    // than relying on the tab closing. Keep the
                                    // sidebar width preference — it's a device
                                    // setting, not session data.
                                    const collapsedPref = window.localStorage.getItem('sidebarCollapsed')
                                    window.localStorage.clear()
                                    if (collapsedPref) window.localStorage.setItem('sidebarCollapsed', collapsedPref)
                                }}
                            >
                                <img src={logout} alt="" />
                                <span>Logout</span>
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Sidebar
